import React, { ReactNode } from "react";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PaginacaoPadrao from "../Paginacao/PaginacaoPadrao";

export interface ColunaTabela<T = any> {
  key: string;
  label: ReactNode;
  render?: (value: any, row: T) => ReactNode;
  width?: number | string;
  align?: "left" | "right" | "center";
}

interface CustomTableProps<T> {
  columns: ColunaTabela<T>[];
  data: T[];
  idField: keyof T;
  onVisualizar?: (row: T) => void;
  onEditar?: (row: T) => void;
  onDelete?: (row: T) => void;
  // Ações adicionais exibidas após Visualizar/Editar/Excluir (ex.: downloads)
  acoesExtras?: (row: T) => ReactNode;
  acoesNoInicio?: boolean;
  textoAcoes?: string;
  paginaAtual?: number;
  totalPaginas?: number;
  setPaginaAtual?: (pagina: number) => void;
  maxHeight?: number | string;
  carregando?: boolean;
  mensagemVazia?: string;
}

// Tabela padrão das listagens (mesmo padrão visual do XC WMS)
function CustomTable<T extends Record<string, any>>({
  columns,
  data,
  idField,
  onVisualizar,
  onEditar,
  onDelete,
  acoesExtras,
  acoesNoInicio = true,
  textoAcoes = "Ações",
  paginaAtual,
  totalPaginas,
  setPaginaAtual,
  maxHeight = "60vh",
  carregando = false,
  mensagemVazia = "Nenhum registro encontrado",
}: CustomTableProps<T>) {
  const theme = useTheme();

  const temAcoes = Boolean(onVisualizar || onEditar || onDelete || acoesExtras);

  const cabecalhoAcoes = temAcoes && (
    <TableCell align="left" sx={{ paddingLeft: 2, width: 0 }}>
      {textoAcoes}
    </TableCell>
  );

  const renderAcoes = (row: T) => (
    <TableCell align="left" sx={{ paddingLeft: 2 }}>
      {onVisualizar && (
        <Tooltip title="Visualizar" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onVisualizar(row);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onEditar && (
        <Tooltip title="Editar" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEditar(row);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title="Excluir" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {acoesExtras && acoesExtras(row)}
    </TableCell>
  );

  const totalColunas = columns.length + (temAcoes ? 1 : 0);

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          marginLeft: 2,
          marginRight: 2,
          marginBottom: 2,
          width: "auto",
          overflowX: "auto",
          overflowY: "auto",
          whiteSpace: "nowrap",
          maxHeight,
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 650 }}>
          <TableHead
            sx={{
              "& .MuiTableCell-head": {
                backgroundColor: theme.palette.mode === "dark" ? "#28243D" : "#E5E5EA",
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          >
            <TableRow>
              {acoesNoInicio && cabecalhoAcoes}
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || "left"}
                  style={{ width: col.width }}
                >
                  {col.label}
                </TableCell>
              ))}
              {!acoesNoInicio && cabecalhoAcoes}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, rowIndex) => (
              <TableRow hover key={`${row[idField] ?? "sem-id"}-${rowIndex}`}>
                {acoesNoInicio && temAcoes && renderAcoes(row)}
                {columns.map((col) => {
                  const value = row[col.key];
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align || "left"}
                      sx={{ verticalAlign: "top" }}
                    >
                      {col.render ? col.render(value, row) : value}
                    </TableCell>
                  );
                })}
                {!acoesNoInicio && temAcoes && renderAcoes(row)}
              </TableRow>
            ))}
            {data.length === 0 && !carregando && (
              <TableRow>
                <TableCell
                  colSpan={totalColunas}
                  align="center"
                  sx={{ py: 4, color: theme.palette.text.secondary }}
                >
                  {mensagemVazia}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {paginaAtual && totalPaginas && setPaginaAtual && totalPaginas > 0 ? (
        <PaginacaoPadrao
          paginaAtual={paginaAtual}
          totalPaginas={totalPaginas}
          setPaginaAtual={setPaginaAtual}
        />
      ) : null}
    </>
  );
}

export default CustomTable;
